import type { AxiosRequestConfig } from "axios";

import { fetchAiEngineData } from "scmp-app/lib/ai-engine/apis";
import { AiEngineDataType, OnBoardingType } from "scmp-app/lib/ai-engine/enums";
import type { AiOnBoardResponse } from "scmp-app/lib/ai-engine/types";

import type { OnboardingSlide, RecommendedEntityIds, RecommendedOnboardingFunction } from "./types";

export const axiosOptions: AxiosRequestConfig = {
  withCredentials: true,
};

export const generateRecommendedPromises = (
  onboardingSlides: OnboardingSlide[],
  scmpUserUuid: string,
) => {
  const fetchProvider: Partial<Record<OnboardingSlide, RecommendedOnboardingFunction>> = {
    conversations: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.Conversations]
        >({
          sc: OnBoardingType.Conversations,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(article => article.id) ?? [],
          },
          key: "conversations",
        };
      },
    },
    evergreenArticles: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.EvergreenArticles]
        >({
          sc: OnBoardingType.EvergreenArticles,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(article => article.id) ?? [],
          },
          key: "evergreenArticles",
        };
      },
    },
    myNews: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.MyNews]
        >({
          sc: OnBoardingType.MyNews,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            topicEntityIds: recommend.data?.topic?.map?.(topic => topic.id) ?? [],
          },
          key: "myNews",
        };
      },
    },
    recommendedArticles: {
      fetcher: async () => {
        const recommend = await fetchAiEngineData<
          AiEngineDataType.OnBoard,
          AiOnBoardResponse[OnBoardingType.RecommendedArticles]
        >({
          sc: OnBoardingType.RecommendedArticles,
          type: AiEngineDataType.OnBoard,
          userUuid: scmpUserUuid,
        });
        return {
          data: {
            articleEntityIds: recommend.data?.article?.map?.(topic => topic.id) ?? [],
          },
          key: "recommendedArticles",
        };
      },
    },
  };

  const fetchers = onboardingSlides.reduce<(() => RecommendedOnboardingFunction["fetcher"])[]>(
    (array, key) => {
      const data = fetchProvider[key];
      if (data) array.push(() => data.fetcher);
      return array;
    },
    [],
  );

  return fetchers;
};

export const sanitizePromisesToMap = (resolvedPromises: RecommendedEntityIds[]) => {
  if (resolvedPromises.length === 0) return null;
  return resolvedPromises.reduce(
    (accumulator, element) => ({
      ...accumulator,
      [element.key]: {
        articleEntityIds: element.data.articleEntityIds ?? [],
        authorEntityIds: element.data.authorEntityIds ?? [],
        topicEntityIds: element.data.topicEntityIds ?? [],
      },
    }),
    {} as Record<OnboardingSlide, Required<RecommendedEntityIds["data"]>>,
  );
};
